import { CubeProvider, QueryRenderer } from '@cubejs-client/react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import MessageIcon from '@mui/icons-material/Message';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import { cubejsApiForOrganization, formatCurrency } from 'app.functions';
import viewOrganization from 'api/Organization/viewOrganization';
import { DateTime } from 'luxon';
import React, { useCallback, useRef, useState } from 'react';
import {
    Bar,
    BarChart,
    Cell,
    Label,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Organization } from 'utils';
import { useNetworkingOrganization } from '../state/NetworkingContext';
import { AnalyticsTooltip } from './AnalyticsTooltip';
import DasboardCard from './DashboardCard';

const PartnershipCommissionsAnalytics: React.FC = () => {
    const organizationDetails = useNetworkingOrganization();
    const theme = useTheme();
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactOrg, setContactOrg] = useState<Organization | null>(null);
    const [contactLoading, setContactLoading] = useState(false);
    const [orgLogos, setOrgLogos] = useState<Record<string, string | null>>({});
    const fetchedUuids = useRef(new Set<string>());

    const fetchOrgLogos = useCallback((uuids: string[]) => {
        const newUuids = uuids.filter((u) => u && !fetchedUuids.current.has(u));
        if (newUuids.length === 0) return;
        newUuids.forEach((u) => fetchedUuids.current.add(u));
        newUuids.forEach(async (uuid) => {
            try {
                const res = await viewOrganization(uuid);
                const url = res.data?.image_urls?.avatar || res.data?.logo_url || null;
                setOrgLogos((prev) => ({ ...prev, [uuid]: url }));
            } catch {
                setOrgLogos((prev) => ({ ...prev, [uuid]: null }));
            }
        });
    }, []);

    const handlePartnerClick = async (orgUuid: string) => {
        setContactLoading(true);
        setContactModalOpen(true);
        try {
            const response = await viewOrganization(orgUuid);
            setContactOrg(response.data);
        } catch {
            setContactOrg(null);
        } finally {
            setContactLoading(false);
        }
    };

    const handleCloseContactModal = () => {
        setContactModalOpen(false);
        setContactOrg(null);
    };

    return organizationDetails.data ? (
        <CubeProvider cubejsApi={cubejsApiForOrganization}>
            <QueryRenderer
                query={{
                    measures: ['FactCommissions.totalAmount'],
                    timeDimensions: [
                        {
                            dimension: 'FactCommissions.earnedDate',
                            granularity: 'day',
                            dateRange: [
                                DateTime.now().minus({ year: 1 }).startOf('month').toISODate(),
                                DateTime.now().endOf('month').toISODate(),
                            ],
                        },
                    ],
                    order: {
                        'FactCommissions.earnedDate': 'desc',
                    },
                    filters: [
                        {
                            member: 'DimOrganization.uuid',
                            operator: 'equals',
                            values: [organizationDetails.data.uuid],
                        },
                    ],
                }}
                render={({ resultSet }) => {
                    // Always render, use empty data if resultSet is null
                    const dailyData = resultSet
                        ? resultSet.tablePivot({ fillMissingDates: true })
                        : [];
                    // Calculate period totals
                    const last30Days = dailyData
                        .slice(-30)
                        .reduce(
                            (sum, row) =>
                                sum + (Number(row['FactCommissions.totalAmount']) / 100 || 0),
                            0,
                        );
                    const previous30Days = dailyData
                        .slice(-60, -30)
                        .reduce(
                            (sum, row) =>
                                sum + (Number(row['FactCommissions.totalAmount']) / 100 || 0),
                            0,
                        );
                    const last60Days = dailyData
                        .slice(-60)
                        .reduce(
                            (sum, row) =>
                                sum + (Number(row['FactCommissions.totalAmount']) / 100 || 0),
                            0,
                        );
                    const previous60Days = dailyData
                        .slice(-120, -60)
                        .reduce(
                            (sum, row) =>
                                sum + (Number(row['FactCommissions.totalAmount']) / 100 || 0),
                            0,
                        );
                    const last90Days = dailyData
                        .slice(-90)
                        .reduce(
                            (sum, row) =>
                                sum + (Number(row['FactCommissions.totalAmount']) / 100 || 0),
                            0,
                        );
                    const previous90Days = dailyData
                        .slice(-180, -90)
                        .reduce(
                            (sum, row) =>
                                sum + (Number(row['FactCommissions.totalAmount']) / 100 || 0),
                            0,
                        );

                    // Calculate percent changes
                    const percent30Days = previous30Days
                        ? ((last30Days - previous30Days) / previous30Days) * 100
                        : 0;
                    const percent60Days = previous60Days
                        ? ((last60Days - previous60Days) / previous60Days) * 100
                        : 0;
                    const percent90Days = previous90Days
                        ? ((last90Days - previous90Days) / previous90Days) * 100
                        : 0;

                    // Transform data for chart — filter to last 7 full months
                    const chartCutoff = DateTime.now().minus({ months: 6 }).startOf('month');
                    const chartData = dailyData
                        .filter((row) => {
                            const d = DateTime.fromISO(
                                row['FactCommissions.earnedDate.day'] as string,
                            );
                            return d >= chartCutoff;
                        })
                        .reduce((acc: any[], row) => {
                            const date = DateTime.fromISO(
                                row['FactCommissions.earnedDate.day'] as string,
                            );
                            const monthKey = date.toFormat('LLL yyyy');
                            const amount = Number(row['FactCommissions.totalAmount']) / 100 || 0;

                            const existingMonth = acc.find((item) => item.date === monthKey);
                            if (existingMonth) {
                                existingMonth.amount += amount;
                            } else {
                                acc.push({ date: monthKey, amount });
                            }
                            return acc;
                        }, []);

                    return (
                        <>
                            {/* Top Row: Total Commissions Earned, Top Earnings, and Chart */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: '1.2fr 1fr 1.5fr',
                                    },
                                    gap: 4,
                                    mb: 6,
                                    mt: 2,
                                }}
                            >
                                <QueryRenderer
                                    query={{
                                        measures: ['FactCommissions.totalAmount'],
                                        filters: [
                                            {
                                                member: 'DimOrganization.uuid',
                                                operator: 'equals',
                                                values: [organizationDetails.data?.uuid || ''],
                                            },
                                        ],
                                    }}
                                    render={({ resultSet }) => {
                                        // Always render, show 0 if no data
                                        const totalAmount = resultSet
                                            ? resultSet
                                                  .tablePivot()
                                                  .reduce(
                                                      (sum, row) =>
                                                          sum +
                                                          Number(
                                                              row['FactCommissions.totalAmount'],
                                                          ) /
                                                              100,
                                                      0,
                                                  )
                                            : 0;

                                        return (
                                            <Paper
                                                elevation={2}
                                                sx={{
                                                    overflow: 'hidden',
                                                    borderRadius: 2,
                                                    p: 4,
                                                    background: `linear-gradient(135deg, #111111 0%, #1A1A1A 40%, #222222 100%)`,
                                                    borderTop: '3px solid #69BE28',
                                                    boxShadow: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: {
                                                                xs: '1.2rem',
                                                                md: '1.5rem',
                                                            },
                                                            fontWeight: 900,
                                                            color: '#69BE28',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '1.5px',
                                                        }}
                                                    >
                                                        🔥🔥🔥 Total Commissions
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        mt: 1.5,
                                                        fontSize: {
                                                            xs: '2.5rem',
                                                            md: '3.5rem',
                                                        },
                                                        fontWeight: 900,
                                                        lineHeight: 1,
                                                        color: '#FFFFFF',
                                                        display: 'flex',
                                                        alignItems: 'baseline',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {formatCurrency({
                                                        amount: totalAmount,
                                                        currency: 'USD',
                                                    })}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        mt: 1,
                                                        fontSize: '0.85rem',
                                                        color: 'rgba(255,255,255,0.6)',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Your lifetime earnings from all commission types
                                                </Typography>
                                                <QueryRenderer
                                                    query={{
                                                        measures: ['FactCommissions.totalAmount'],
                                                        dimensions: [
                                                            'DimOtherOrganization.uuid',
                                                            'DimOtherOrganization.name',
                                                        ],
                                                        filters: [
                                                            {
                                                                member: 'DimOrganization.uuid',
                                                                operator: 'equals',
                                                                values: [
                                                                    organizationDetails?.data
                                                                        ?.uuid || '',
                                                                ],
                                                            },
                                                            {
                                                                member: 'DimOtherOrganization.uuid',
                                                                operator: 'notEquals',
                                                                values: [
                                                                    organizationDetails?.data
                                                                        ?.uuid || '',
                                                                ],
                                                            },
                                                            {
                                                                member: 'DimOtherOrganization.name',
                                                                operator: 'set',
                                                            },
                                                        ],
                                                        order: {
                                                            'FactCommissions.totalAmount': 'desc',
                                                        },
                                                        limit: 10,
                                                    }}
                                                    render={({ resultSet }) => {
                                                        const topPartners = resultSet
                                                            ? resultSet.tablePivot().map((row) => ({
                                                                  orgUuid:
                                                                      (row[
                                                                          'DimOtherOrganization.uuid'
                                                                      ] as string) || '',
                                                                  partnerName:
                                                                      row[
                                                                          'DimOtherOrganization.name'
                                                                      ],
                                                                  totalAmount:
                                                                      Number(
                                                                          row[
                                                                              'FactCommissions.totalAmount'
                                                                          ],
                                                                      ) / 100,
                                                              }))
                                                            : [];

                                                        fetchOrgLogos(
                                                            topPartners.map((p) => p.orgUuid),
                                                        );

                                                        return (
                                                            <Box
                                                                sx={{
                                                                    mt: 3,
                                                                    pt: 2.5,
                                                                    borderTop:
                                                                        '1px solid rgba(105,190,40,0.3)',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                        color: '#69BE28',
                                                                        fontSize: '0.9rem',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '1px',
                                                                        mb: 1.5,
                                                                    }}
                                                                >
                                                                    Top Earnings By Partner
                                                                </Typography>
                                                                {topPartners.length > 0 ? (
                                                                    topPartners.map(
                                                                        (partner, index) => (
                                                                            <Box
                                                                                key={
                                                                                    (partner.partnerName as string) +
                                                                                    index.toString()
                                                                                }
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'space-between',
                                                                                    py: 1,
                                                                                    borderBottom:
                                                                                        '1px solid rgba(255,255,255,0.1)',
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        sx={{
                                                                                            minWidth: 24,
                                                                                            color: 'rgba(255,255,255,0.5)',
                                                                                            fontWeight:
                                                                                                'bold',
                                                                                            fontSize:
                                                                                                '0.75rem',
                                                                                        }}
                                                                                    >
                                                                                        #{index + 1}
                                                                                    </Typography>
                                                                                    <Avatar
                                                                                        src={
                                                                                            orgLogos[
                                                                                                partner
                                                                                                    .orgUuid
                                                                                            ] ||
                                                                                            undefined
                                                                                        }
                                                                                        alt={
                                                                                            partner.partnerName as string
                                                                                        }
                                                                                        variant="rounded"
                                                                                        sx={{
                                                                                            width: 24,
                                                                                            height: 24,
                                                                                            ml: 0.5,
                                                                                            fontSize:
                                                                                                '0.65rem',
                                                                                            bgcolor:
                                                                                                'rgba(105,190,40,0.25)',
                                                                                            color: '#fff',
                                                                                        }}
                                                                                    >
                                                                                        {(
                                                                                            partner.partnerName as string
                                                                                        )?.charAt(
                                                                                            0,
                                                                                        ) || '?'}
                                                                                    </Avatar>
                                                                                    <Typography
                                                                                        onClick={() =>
                                                                                            partner.orgUuid &&
                                                                                            handlePartnerClick(
                                                                                                partner.orgUuid,
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            ml: 1,
                                                                                            fontSize:
                                                                                                '0.85rem',
                                                                                            fontWeight: 600,
                                                                                            color: '#fff',
                                                                                            cursor: partner.orgUuid
                                                                                                ? 'pointer'
                                                                                                : 'default',
                                                                                            '&:hover':
                                                                                                partner.orgUuid
                                                                                                    ? {
                                                                                                          color: '#69BE28',
                                                                                                          textDecoration:
                                                                                                              'underline',
                                                                                                      }
                                                                                                    : {},
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            partner.partnerName
                                                                                        }
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        whiteSpace:
                                                                                            'nowrap',
                                                                                        fontSize:
                                                                                            '0.85rem',
                                                                                        color: '#69BE28',
                                                                                    }}
                                                                                >
                                                                                    {formatCurrency(
                                                                                        {
                                                                                            amount: partner.totalAmount,
                                                                                            currency:
                                                                                                'USD',
                                                                                        },
                                                                                    )}
                                                                                </Typography>
                                                                            </Box>
                                                                        ),
                                                                    )
                                                                ) : (
                                                                    <Typography
                                                                        sx={{
                                                                            color: 'rgba(255,255,255,0.4)',
                                                                            fontSize: '0.8rem',
                                                                            py: 1,
                                                                        }}
                                                                    >
                                                                        No partner earnings data yet
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    }}
                                                />
                                                <QueryRenderer
                                                    query={{
                                                        measures: ['FactCommissions.totalAmount'],
                                                        dimensions: [
                                                            'DimOtherOrganization.uuid',
                                                            'DimOtherOrganization.name',
                                                        ],
                                                        timeDimensions: [
                                                            {
                                                                dimension:
                                                                    'FactCommissions.earnedDate',
                                                                dateRange: [
                                                                    DateTime.now()
                                                                        .minus({ months: 3 })
                                                                        .toISODate(),
                                                                    DateTime.now().toISODate(),
                                                                ],
                                                            },
                                                        ],
                                                        filters: [
                                                            {
                                                                member: 'DimOrganization.uuid',
                                                                operator: 'equals',
                                                                values: [
                                                                    organizationDetails?.data
                                                                        ?.uuid || '',
                                                                ],
                                                            },
                                                            {
                                                                member: 'DimOtherOrganization.uuid',
                                                                operator: 'notEquals',
                                                                values: [
                                                                    organizationDetails?.data
                                                                        ?.uuid || '',
                                                                ],
                                                            },
                                                            {
                                                                member: 'DimOtherOrganization.name',
                                                                operator: 'set',
                                                            },
                                                        ],
                                                        order: {
                                                            'FactCommissions.totalAmount': 'desc',
                                                        },
                                                        limit: 5,
                                                    }}
                                                    render={({ resultSet }) => {
                                                        const recentPartners = resultSet
                                                            ? resultSet.tablePivot().map((row) => ({
                                                                  orgUuid:
                                                                      (row[
                                                                          'DimOtherOrganization.uuid'
                                                                      ] as string) || '',
                                                                  partnerName:
                                                                      row[
                                                                          'DimOtherOrganization.name'
                                                                      ],
                                                                  totalAmount:
                                                                      Number(
                                                                          row[
                                                                              'FactCommissions.totalAmount'
                                                                          ],
                                                                      ) / 100,
                                                              }))
                                                            : [];

                                                        fetchOrgLogos(
                                                            recentPartners.map((p) => p.orgUuid),
                                                        );

                                                        return (
                                                            <Box
                                                                sx={{
                                                                    mt: 3,
                                                                    pt: 2.5,
                                                                    borderTop:
                                                                        '1px solid rgba(105,190,40,0.3)',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                        color: 'rgba(255,255,255,0.8)',
                                                                        fontSize: '0.9rem',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '1px',
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    Top Earnings By Partner
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        color: 'rgba(255,255,255,0.4)',
                                                                        fontSize: '0.7rem',
                                                                        mb: 1.5,
                                                                        fontStyle: 'italic',
                                                                    }}
                                                                >
                                                                    Last 3 months
                                                                </Typography>
                                                                {recentPartners.length > 0 ? (
                                                                    recentPartners.map(
                                                                        (partner, index) => (
                                                                            <Box
                                                                                key={`${partner.partnerName}recent${index}`}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'space-between',
                                                                                    py: 1,
                                                                                    borderBottom:
                                                                                        '1px solid rgba(255,255,255,0.1)',
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        sx={{
                                                                                            minWidth: 24,
                                                                                            color: 'rgba(255,255,255,0.5)',
                                                                                            fontWeight:
                                                                                                'bold',
                                                                                            fontSize:
                                                                                                '0.75rem',
                                                                                        }}
                                                                                    >
                                                                                        #{index + 1}
                                                                                    </Typography>
                                                                                    <Avatar
                                                                                        src={
                                                                                            orgLogos[
                                                                                                partner
                                                                                                    .orgUuid
                                                                                            ] ||
                                                                                            undefined
                                                                                        }
                                                                                        alt={
                                                                                            partner.partnerName as string
                                                                                        }
                                                                                        variant="rounded"
                                                                                        sx={{
                                                                                            width: 24,
                                                                                            height: 24,
                                                                                            ml: 0.5,
                                                                                            fontSize:
                                                                                                '0.65rem',
                                                                                            bgcolor:
                                                                                                'rgba(105,190,40,0.25)',
                                                                                            color: '#fff',
                                                                                        }}
                                                                                    >
                                                                                        {(
                                                                                            partner.partnerName as string
                                                                                        )?.charAt(
                                                                                            0,
                                                                                        ) || '?'}
                                                                                    </Avatar>
                                                                                    <Typography
                                                                                        onClick={() =>
                                                                                            partner.orgUuid &&
                                                                                            handlePartnerClick(
                                                                                                partner.orgUuid,
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            ml: 1,
                                                                                            fontSize:
                                                                                                '0.85rem',
                                                                                            fontWeight: 600,
                                                                                            color: '#fff',
                                                                                            cursor: partner.orgUuid
                                                                                                ? 'pointer'
                                                                                                : 'default',
                                                                                            '&:hover':
                                                                                                partner.orgUuid
                                                                                                    ? {
                                                                                                          color: '#69BE28',
                                                                                                          textDecoration:
                                                                                                              'underline',
                                                                                                      }
                                                                                                    : {},
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            partner.partnerName
                                                                                        }
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        whiteSpace:
                                                                                            'nowrap',
                                                                                        fontSize:
                                                                                            '0.85rem',
                                                                                        color: '#69BE28',
                                                                                    }}
                                                                                >
                                                                                    {formatCurrency(
                                                                                        {
                                                                                            amount: partner.totalAmount,
                                                                                            currency:
                                                                                                'USD',
                                                                                        },
                                                                                    )}
                                                                                </Typography>
                                                                            </Box>
                                                                        ),
                                                                    )
                                                                ) : (
                                                                    <Typography
                                                                        sx={{
                                                                            color: 'rgba(255,255,255,0.4)',
                                                                            fontSize: '0.8rem',
                                                                            py: 1,
                                                                        }}
                                                                    >
                                                                        No recent earnings data
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    }}
                                                />
                                            </Paper>
                                        );
                                    }}
                                />
                                <QueryRenderer
                                    query={{
                                        measures: ['FactCommissions.totalAmount'],
                                        dimensions: [
                                            'DimOtherOrganization.uuid',
                                            'DimOtherOrganization.name',
                                            'DimBonus.type',
                                        ],
                                        filters: [
                                            {
                                                member: 'DimOrganization.uuid',
                                                operator: 'equals',
                                                values: [organizationDetails?.data?.uuid || ''],
                                            },
                                            {
                                                member: 'DimOtherOrganization.uuid',
                                                operator: 'notEquals',
                                                values: [organizationDetails?.data?.uuid || ''],
                                            },
                                            {
                                                member: 'DimOtherOrganization.name',
                                                operator: 'set',
                                            },
                                        ],
                                        order: {
                                            'FactCommissions.totalAmount': 'desc',
                                        },
                                        limit: 10,
                                    }}
                                    render={({ resultSet }) => {
                                        const SELLING_TYPES = new Set([
                                            'Organization Subscription - Initial',
                                            'Organization Subscription - Recurring',
                                        ]);

                                        const topPartners = resultSet
                                            ? resultSet.tablePivot().map((row) => ({
                                                  orgUuid:
                                                      (row[
                                                          'DimOtherOrganization.uuid'
                                                      ] as string) || '',
                                                  partnerName: row['DimOtherOrganization.name'],
                                                  commissionType: row['DimBonus.type'] as string,
                                                  totalAmount:
                                                      Number(row['FactCommissions.totalAmount']) /
                                                      100,
                                                  isRecruiting: !SELLING_TYPES.has(
                                                      row['DimBonus.type'] as string,
                                                  ),
                                              }))
                                            : [];

                                        fetchOrgLogos(topPartners.map((p) => p.orgUuid));

                                        const recruiting = topPartners
                                            .filter((p) => p.isRecruiting)
                                            .reduce((s, p) => s + p.totalAmount, 0);
                                        const selling = topPartners
                                            .filter((p) => !p.isRecruiting)
                                            .reduce((s, p) => s + p.totalAmount, 0);
                                        const total = recruiting + selling;
                                        const pieData = [
                                            {
                                                name: 'Recruiting',
                                                value: recruiting,
                                                color: '#1A1A1A',
                                            },
                                            { name: 'Selling', value: selling, color: '#69BE28' },
                                        ].filter((d) => d.value > 0);

                                        return (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 2,
                                                    p: 3,
                                                    boxShadow: 2,
                                                    borderTop: '3px solid #69BE28',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <Typography variant="h6" gutterBottom>
                                                    Top Earnings By Partner And Type
                                                </Typography>
                                                {total > 0 && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            mb: 2,
                                                            pb: 2,
                                                            borderBottom: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    >
                                                        <ResponsiveContainer
                                                            width={140}
                                                            height={140}
                                                        >
                                                            <PieChart>
                                                                <Pie
                                                                    data={pieData}
                                                                    dataKey="value"
                                                                    nameKey="name"
                                                                    cx="50%"
                                                                    cy="50%"
                                                                    outerRadius={60}
                                                                    innerRadius={35}
                                                                    strokeWidth={2}
                                                                >
                                                                    {pieData.map((entry) => (
                                                                        <Cell
                                                                            key={entry.name}
                                                                            fill={entry.color}
                                                                        />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip
                                                                    formatter={(value: number) =>
                                                                        formatCurrency({
                                                                            amount: value,
                                                                            currency: 'USD',
                                                                        })
                                                                    }
                                                                />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    mb: 1,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                    fontSize: '0.7rem',
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                Revenue Sources
                                                            </Typography>
                                                            {pieData.map((entry) => (
                                                                <Box
                                                                    key={entry.name}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                        py: 0.75,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 10,
                                                                                height: 10,
                                                                                borderRadius: '50%',
                                                                                bgcolor:
                                                                                    entry.color,
                                                                            }}
                                                                        />
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontWeight={600}
                                                                        >
                                                                            {entry.name}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="bold"
                                                                    >
                                                                        {formatCurrency({
                                                                            amount: entry.value,
                                                                            currency: 'USD',
                                                                        })}
                                                                        <Typography
                                                                            component="span"
                                                                            variant="caption"
                                                                            sx={{
                                                                                ml: 0.5,
                                                                                color: 'text.secondary',
                                                                            }}
                                                                        >
                                                                            (
                                                                            {total > 0
                                                                                ? (
                                                                                      (entry.value /
                                                                                          total) *
                                                                                      100
                                                                                  ).toFixed(0)
                                                                                : 0}
                                                                            %)
                                                                        </Typography>
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    display: 'block',
                                                                    color: 'text.disabled',
                                                                    lineHeight: 1.3,
                                                                }}
                                                            >
                                                                Recruiting = all commissions except
                                                                org subscriptions
                                                                <br />
                                                                Selling = org subscription
                                                                commissions
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                                    {topPartners.length > 0 ? (
                                                        topPartners.map((partner, index) => (
                                                            <Box
                                                                key={
                                                                    partner.partnerName +
                                                                    partner.commissionType +
                                                                    index.toString()
                                                                }
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    py: 1.5,
                                                                    borderBottom: '1px solid',
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 6,
                                                                            height: 28,
                                                                            borderRadius: 1,
                                                                            bgcolor:
                                                                                partner.isRecruiting
                                                                                    ? '#1A1A1A'
                                                                                    : '#69BE28',
                                                                            mr: 1,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            minWidth: 30,
                                                                            color: 'text.secondary',
                                                                            fontWeight: 'bold',
                                                                        }}
                                                                    >
                                                                        #{index + 1}
                                                                    </Typography>
                                                                    <Avatar
                                                                        src={
                                                                            orgLogos[
                                                                                partner.orgUuid
                                                                            ] || undefined
                                                                        }
                                                                        alt={
                                                                            partner.partnerName as string
                                                                        }
                                                                        variant="rounded"
                                                                        sx={{
                                                                            width: 28,
                                                                            height: 28,
                                                                            ml: 1,
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        {(
                                                                            partner.partnerName as string
                                                                        )?.charAt(0) || '?'}
                                                                    </Avatar>
                                                                    <Box sx={{ ml: 1 }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            onClick={() =>
                                                                                partner.orgUuid &&
                                                                                handlePartnerClick(
                                                                                    partner.orgUuid,
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                cursor: partner.orgUuid
                                                                                    ? 'pointer'
                                                                                    : 'default',
                                                                                '&:hover':
                                                                                    partner.orgUuid
                                                                                        ? {
                                                                                              textDecoration:
                                                                                                  'underline',
                                                                                          }
                                                                                        : {},
                                                                            }}
                                                                        >
                                                                            {partner.partnerName}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {partner.commissionType}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {formatCurrency({
                                                                        amount: partner.totalAmount,
                                                                        currency: 'USD',
                                                                    })}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'text.secondary',
                                                            }}
                                                        >
                                                            No partner earnings data available yet
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                />
                                <Box
                                    sx={{
                                        height: '100%',
                                        minHeight: 300,
                                        bgcolor: 'background.paper',
                                        borderRadius: 2,
                                        p: 3,
                                        boxShadow: 2,
                                        borderTop: '3px solid #69BE28',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="h6" gutterBottom>
                                        Earnings By Month
                                    </Typography>
                                    <Box sx={{ flex: 1, mt: 2 }}>
                                        {chartData.some((row) => row.amount > 0) ? (
                                            (() => {
                                                const amounts = chartData.map((d) => d.amount);
                                                const maxVal = Math.max(...amounts);
                                                const minVal = Math.min(...amounts);
                                                const domainMin = Math.floor(minVal * 0.9);
                                                const domainMax = Math.ceil(maxVal * 1.1);
                                                return (
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={chartData}>
                                                            <XAxis dataKey="date">
                                                                <Label
                                                                    value="Month"
                                                                    position="insideBottom"
                                                                    offset={-5}
                                                                    style={{
                                                                        fontSize: '0.75rem',
                                                                        fill: '#666',
                                                                    }}
                                                                />
                                                            </XAxis>
                                                            <YAxis
                                                                domain={[domainMin, domainMax]}
                                                                tickFormatter={(v: number) =>
                                                                    `$${v.toLocaleString()}`
                                                                }
                                                            >
                                                                <Label
                                                                    value="Amount (USD)"
                                                                    angle={-90}
                                                                    position="insideLeft"
                                                                    style={{
                                                                        textAnchor: 'middle',
                                                                        fontSize: '0.75rem',
                                                                        fill: '#666',
                                                                    }}
                                                                />
                                                            </YAxis>
                                                            <Tooltip
                                                                content={<AnalyticsTooltip />}
                                                            />
                                                            <Bar
                                                                dataKey="amount"
                                                                fill="#69BE28"
                                                                radius={[4, 4, 0, 0]}
                                                            />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                );
                                            })()
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                No earnings data available yet
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            {/* Second Row: Earned Last 30/60 Days, Earned Last 90 Days, Total Unpaid */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: '1fr 1fr 1fr 1fr',
                                    },
                                    gap: 4,
                                    mb: 4,
                                }}
                            >
                                <DasboardCard
                                    sx={{
                                        borderLeft: '4px solid #69BE28',
                                        boxShadow: 2,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 20px rgba(105,190,40,0.15)',
                                        },
                                    }}
                                    title="Earned Last 30 Days"
                                    value={formatCurrency({
                                        amount: last30Days,
                                        currency: 'USD',
                                    })}
                                    subtext={`${
                                        percent30Days >= 0 ? '+' : ''
                                    }${percent30Days.toFixed(1)}% vs previous period`}
                                    icon={
                                        percent30Days === 0 ? undefined : percent30Days > 0 ? (
                                            <ArrowUpwardIcon color="success" />
                                        ) : (
                                            <ArrowDownwardIcon color="error" />
                                        )
                                    }
                                />
                                <DasboardCard
                                    sx={{
                                        borderLeft: '4px solid #69BE28',
                                        boxShadow: 2,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 20px rgba(105,190,40,0.15)',
                                        },
                                    }}
                                    title="Earned Last 60 Days"
                                    value={formatCurrency({
                                        amount: last60Days,
                                        currency: 'USD',
                                    })}
                                    subtext={`${
                                        percent60Days >= 0 ? '+' : ''
                                    }${percent60Days.toFixed(1)}% vs previous period`}
                                    icon={
                                        percent60Days === 0 ? undefined : percent60Days > 0 ? (
                                            <ArrowUpwardIcon color="success" />
                                        ) : (
                                            <ArrowDownwardIcon color="error" />
                                        )
                                    }
                                />
                                <DasboardCard
                                    sx={{
                                        borderLeft: '4px solid #69BE28',
                                        boxShadow: 2,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 20px rgba(105,190,40,0.15)',
                                        },
                                    }}
                                    title="Earned Last 90 Days"
                                    value={formatCurrency({
                                        amount: last90Days,
                                        currency: 'USD',
                                    })}
                                    subtext={`${
                                        percent90Days >= 0 ? '+' : ''
                                    }${percent90Days.toFixed(1)}% vs previous period`}
                                    icon={
                                        percent90Days === 0 ? undefined : percent90Days > 0 ? (
                                            <ArrowUpwardIcon color="success" />
                                        ) : (
                                            <ArrowDownwardIcon color="error" />
                                        )
                                    }
                                />
                                <QueryRenderer
                                    query={{
                                        measures: ['FactCommissions.totalAmount'],
                                        filters: [
                                            {
                                                member: 'DimOrganization.uuid',
                                                operator: 'equals',
                                                values: [organizationDetails.data?.uuid || ''],
                                            },
                                            {
                                                member: 'FactCommissions.isPaid',
                                                operator: 'equals',
                                                values: ['false'],
                                            },
                                        ],
                                    }}
                                    render={({ resultSet }) => {
                                        // Always render, show 0 if no data
                                        const unpaidAmount = resultSet
                                            ? resultSet
                                                  .tablePivot()
                                                  .reduce(
                                                      (sum, row) =>
                                                          sum +
                                                          Number(
                                                              row['FactCommissions.totalAmount'],
                                                          ) /
                                                              100,
                                                      0,
                                                  )
                                            : 0;

                                        return (
                                            <DasboardCard
                                                sx={{
                                                    borderLeft: '4px solid #69BE28',
                                                    boxShadow: 2,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow:
                                                            '0 4px 20px rgba(105,190,40,0.15)',
                                                    },
                                                }}
                                                title="Total Unpaid Commissions"
                                                value={formatCurrency({
                                                    amount: unpaidAmount,
                                                    currency: 'USD',
                                                })}
                                            />
                                        );
                                    }}
                                />
                            </Box>
                            {/* Third Row: Charts and Breakdowns */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: '1fr 1fr 1fr',
                                    },
                                    gap: 6,
                                    mb: 4,
                                }}
                            >
                                <QueryRenderer
                                    query={{
                                        measures: ['FactCommissions.totalAmount'],
                                        dimensions: ['DimBonus.type'],
                                        filters: [
                                            {
                                                member: 'DimOrganization.uuid',
                                                operator: 'equals',
                                                values: [organizationDetails?.data?.uuid || ''],
                                            },
                                        ],
                                    }}
                                    render={({ resultSet }) => {
                                        // Always render, show empty chart if no data
                                        const bonusData = resultSet ? resultSet.tablePivot() : [];
                                        const total =
                                            bonusData.length > 0
                                                ? bonusData.reduce(
                                                      (sum, row) =>
                                                          sum +
                                                          Number(
                                                              row['FactCommissions.totalAmount'],
                                                          ) /
                                                              100,
                                                      0,
                                                  )
                                                : 1; // Use 1 to avoid division by zero

                                        const pieData =
                                            bonusData.length > 0
                                                ? bonusData.map((row) => ({
                                                      name: `${row['DimBonus.type']} `,
                                                      value:
                                                          (Number(
                                                              row['FactCommissions.totalAmount'],
                                                          ) /
                                                              100 /
                                                              total) *
                                                          100,
                                                  }))
                                                : [{ name: 'No Data', value: 100 }];

                                        const COLORS = [
                                            '#69BE28',
                                            '#1A1A1A',
                                            '#4A8C1C',
                                            '#333333',
                                            '#8BC34A',
                                        ];

                                        return (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 2,
                                                    p: 3,
                                                    boxShadow: 2,
                                                    borderTop: '3px solid #69BE28',
                                                }}
                                            >
                                                <h3>Commission Types Breakdown</h3>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        height: 300,
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box sx={{ width: '100%', height: '100%' }}>
                                                        {pieData.length > 0 ? (
                                                            <ResponsiveContainer
                                                                width="100%"
                                                                height="100%"
                                                            >
                                                                <PieChart>
                                                                    <Pie
                                                                        data={pieData}
                                                                        dataKey="value"
                                                                        nameKey="name"
                                                                        cx="50%"
                                                                        cy="50%"
                                                                        label={false}
                                                                    >
                                                                        {pieData.map(
                                                                            (entry, index) => (
                                                                                <Cell
                                                                                    key={`cell-${index}`}
                                                                                    fill={
                                                                                        COLORS[
                                                                                            index %
                                                                                                COLORS.length
                                                                                        ]
                                                                                    }
                                                                                />
                                                                            ),
                                                                        )}
                                                                    </Pie>
                                                                    <Tooltip
                                                                        formatter={(
                                                                            value,
                                                                            name,
                                                                        ) => [
                                                                            `${
                                                                                typeof value ===
                                                                                'number'
                                                                                    ? value.toFixed(
                                                                                          1,
                                                                                      )
                                                                                    : value
                                                                            }%`,
                                                                            name,
                                                                        ]}
                                                                    />
                                                                    <Legend
                                                                        layout="vertical"
                                                                        align="right"
                                                                        verticalAlign="middle"
                                                                        formatter={(
                                                                            value,
                                                                            entry,
                                                                        ) => (
                                                                            <span
                                                                                style={{
                                                                                    fontSize:
                                                                                        '0.650rem',
                                                                                }}
                                                                            >
                                                                                {value}:{' '}
                                                                                {entry.payload!.value.toFixed(
                                                                                    1,
                                                                                )}
                                                                                %
                                                                            </span>
                                                                        )}
                                                                        wrapperStyle={{
                                                                            paddingLeft: '20px',
                                                                            maxWidth: '40%',
                                                                            overflowWrap:
                                                                                'break-word',
                                                                            maxHeight: '300px',
                                                                            overflowY: 'auto',
                                                                        }}
                                                                    />
                                                                </PieChart>
                                                            </ResponsiveContainer>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography color="text.secondary">
                                                                    No commission data available
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                />
                                <QueryRenderer
                                    query={{
                                        measures: ['FactCommissions.totalAmount'],
                                        dimensions: ['FactCommissions.athleteUserKey'],
                                        filters: [
                                            {
                                                member: 'DimOrganization.uuid',
                                                operator: 'equals',
                                                values: [organizationDetails?.data?.uuid || ''],
                                            },
                                        ],
                                    }}
                                    render={({ resultSet }) => {
                                        // Always render, show empty chart if no data
                                        const salesData = resultSet ? resultSet.tablePivot() : [];
                                        const total =
                                            salesData.length > 0
                                                ? salesData.reduce(
                                                      (sum, row) =>
                                                          sum +
                                                          Number(
                                                              row['FactCommissions.totalAmount'],
                                                          ) /
                                                              100,
                                                      0,
                                                  )
                                                : 1; // Use 1 to avoid division by zero

                                        const athleteSales =
                                            salesData.length > 0
                                                ? salesData
                                                      .filter(
                                                          (row) =>
                                                              row['FactCommissions.athleteUserKey'],
                                                      )
                                                      .reduce(
                                                          (sum, row) =>
                                                              sum +
                                                              Number(
                                                                  row[
                                                                      'FactCommissions.totalAmount'
                                                                  ],
                                                              ) /
                                                                  100,
                                                          0,
                                                      )
                                                : 0;

                                        const orgSales =
                                            salesData.length > 0
                                                ? salesData
                                                      .filter(
                                                          (row) =>
                                                              !row[
                                                                  'FactCommissions.athleteUserKey'
                                                              ],
                                                      )
                                                      .reduce(
                                                          (sum, row) =>
                                                              sum +
                                                              Number(
                                                                  row[
                                                                      'FactCommissions.totalAmount'
                                                                  ],
                                                              ) /
                                                                  100,
                                                          0,
                                                      )
                                                : 0;

                                        const pieData =
                                            total > 0
                                                ? [
                                                      {
                                                          name: 'Athlete Sales',
                                                          value: (athleteSales / total) * 100,
                                                      },
                                                      {
                                                          name: 'Organization Sales',
                                                          value: (orgSales / total) * 100,
                                                      },
                                                  ]
                                                : [{ name: 'No Data', value: 100 }];

                                        const COLORS = ['#69BE28', '#1A1A1A'];

                                        return (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 2,
                                                    p: 3,
                                                    boxShadow: 2,
                                                    borderTop: '3px solid #69BE28',
                                                }}
                                            >
                                                <h3>Sales Source Breakdown</h3>
                                                <Box sx={{ height: 300 }}>
                                                    {pieData.length > 0 ? (
                                                        <ResponsiveContainer>
                                                            <PieChart>
                                                                <Pie
                                                                    data={pieData}
                                                                    dataKey="value"
                                                                    nameKey="name"
                                                                    cx="50%"
                                                                    cy="50%"
                                                                    label={false}
                                                                >
                                                                    {pieData.map((entry, index) => (
                                                                        <Cell
                                                                            key={`cell-${index}`}
                                                                            fill={
                                                                                COLORS[
                                                                                    index %
                                                                                        COLORS.length
                                                                                ]
                                                                            }
                                                                        />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip
                                                                    formatter={(value, name) => [
                                                                        `${
                                                                            typeof value ===
                                                                            'number'
                                                                                ? value.toFixed(1)
                                                                                : value
                                                                        }%`,
                                                                        name,
                                                                    ]}
                                                                />
                                                                <Legend
                                                                    layout="vertical"
                                                                    align="right"
                                                                    verticalAlign="middle"
                                                                    formatter={(value, entry) => (
                                                                        <span
                                                                            style={{
                                                                                fontSize:
                                                                                    '0.650rem',
                                                                            }}
                                                                        >
                                                                            {value}:{' '}
                                                                            {!entry.payload!.value
                                                                                ? '-'
                                                                                : entry.payload!.value.toFixed(
                                                                                      1,
                                                                                  )}
                                                                            %
                                                                        </span>
                                                                    )}
                                                                    wrapperStyle={{
                                                                        paddingLeft: '20px',
                                                                        maxWidth: '40%',
                                                                        overflowWrap: 'break-word',
                                                                        maxHeight: '300px',
                                                                        overflowY: 'auto',
                                                                    }}
                                                                />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Typography color="text.secondary">
                                                                No sales data available
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                />
                                <QueryRenderer
                                    query={{
                                        measures: ['FactCommissions.totalAmount'],
                                        dimensions: ['DimUser.userName'],
                                        filters: [
                                            {
                                                member: 'DimOrganization.uuid',
                                                operator: 'equals',
                                                values: [organizationDetails?.data?.uuid || ''],
                                            },
                                            {
                                                member: 'FactCommissions.athleteUserKey',
                                                operator: 'set',
                                            },
                                            {
                                                member: 'DimUser.uuid',
                                                operator: 'set',
                                            },
                                        ],
                                        order: {
                                            'FactCommissions.totalAmount': 'desc',
                                        },
                                        limit: 5,
                                    }}
                                    render={({ resultSet }) => {
                                        // Always render, show empty list if no data
                                        const athleteData = resultSet ? resultSet.tablePivot() : [];

                                        return (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 2,
                                                    p: 3,
                                                    boxShadow: 2,
                                                    borderTop: '3px solid #69BE28',
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ mb: 2 }}>
                                                    Top 5 Athletes by Earnings
                                                </Typography>
                                                {athleteData.length > 0 ? (
                                                    <Box>
                                                        {athleteData.map((row, index) => (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    py: 1,
                                                                    borderBottom: '1px solid',
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Typography>
                                                                    {row['DimUser.userName']}
                                                                </Typography>
                                                                <Typography>
                                                                    {formatCurrency({
                                                                        amount:
                                                                            Number(
                                                                                row[
                                                                                    'FactCommissions.totalAmount'
                                                                                ],
                                                                            ) / 100,
                                                                        currency: 'USD',
                                                                    })}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            py: 4,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        No athlete earnings data available yet
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    }}
                                />
                            </Box>
                        </>
                    );
                }}
            />
            <Dialog
                open={contactModalOpen}
                onClose={handleCloseContactModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 2,
                        borderBottom: `2px solid #69BE28`,
                        background: `linear-gradient(135deg, #111111 0%, #1A1A1A 100%)`,
                    }}
                >
                    <Typography variant="h5" fontWeight={600} sx={{ color: '#fff' }}>
                        {contactOrg?.name || 'Organization Details'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {contactLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : contactOrg ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    backgroundColor: theme.palette.grey[50],
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        mb: 1,
                                        display: 'block',
                                    }}
                                >
                                    Organization
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {contactOrg.name}
                                </Typography>
                            </Paper>

                            {(contactOrg.owner_first_name ||
                                contactOrg.owner_last_name ||
                                contactOrg.creator) && (
                                <>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 1,
                                                display: 'block',
                                            }}
                                        >
                                            Contact Person
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight={500}
                                            fontSize="1.1rem"
                                        >
                                            {(() => {
                                                const firstName =
                                                    contactOrg.owner_first_name ||
                                                    (contactOrg.creator as any)?.first_name ||
                                                    '';
                                                const lastName =
                                                    contactOrg.owner_last_name ||
                                                    (contactOrg.creator as any)?.last_name ||
                                                    '';
                                                return `${firstName} ${lastName}`.trim() || 'N/A';
                                            })()}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 2,
                                                display: 'block',
                                            }}
                                        >
                                            Phone Number
                                        </Typography>
                                        {(() => {
                                            const phone =
                                                contactOrg.owner_phone ||
                                                (contactOrg.creator as any)?.phone ||
                                                null;
                                            return phone ? (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 2,
                                                        backgroundColor: `${theme.palette.primary.light}08`,
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.primary.light}40`,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 600,
                                                            mb: 2,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {phone}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1.5,
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<VideoCallIcon />}
                                                            onClick={() => {
                                                                window.location.href = `facetime://${phone}`;
                                                            }}
                                                            sx={{
                                                                minWidth: 140,
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                boxShadow: 2,
                                                            }}
                                                        >
                                                            Call
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            startIcon={<MessageIcon />}
                                                            onClick={() => {
                                                                window.location.href = `sms://${phone}`;
                                                            }}
                                                            sx={{
                                                                minWidth: 140,
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                boxShadow: 2,
                                                            }}
                                                        >
                                                            Text
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontStyle="italic"
                                                >
                                                    No phone number available
                                                </Typography>
                                            );
                                        })()}
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 2,
                                                display: 'block',
                                            }}
                                        >
                                            Email Address
                                        </Typography>
                                        {(() => {
                                            const email =
                                                contactOrg.owner_email ||
                                                (contactOrg.creator as any)?.email ||
                                                null;
                                            return email ? (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 2,
                                                        backgroundColor: `${theme.palette.info.light}08`,
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.info.light}40`,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 500,
                                                            mb: 2,
                                                            textAlign: 'center',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {email}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            color="info"
                                                            startIcon={<EmailIcon />}
                                                            onClick={() => {
                                                                window.location.href = `mailto:${email}`;
                                                            }}
                                                            sx={{
                                                                minWidth: 180,
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                boxShadow: 2,
                                                            }}
                                                        >
                                                            Send Email
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontStyle="italic"
                                                >
                                                    No email address available
                                                </Typography>
                                            );
                                        })()}
                                    </Box>
                                </>
                            )}
                            {!contactOrg.owner_first_name &&
                                !contactOrg.owner_last_name &&
                                !contactOrg.creator && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            backgroundColor: `${theme.palette.warning.light}08`,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.warning.light}40`,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <InfoIcon
                                            sx={{
                                                fontSize: 40,
                                                color: 'text.secondary',
                                                mb: 1,
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            No contact information available for this organization
                                        </Typography>
                                    </Paper>
                                )}
                        </Box>
                    ) : (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            Could not load organization details
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseContactModal}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 100,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </CubeProvider>
    ) : (
        <Box />
    );
};

export default PartnershipCommissionsAnalytics;
